import React, { useEffect, useState, useCallback } from "react"
import MetaTags from "react-meta-tags"
import { Container, Row, Col, Card, CardBody, Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap"
import { Link } from "react-router-dom"
import Breadcrumbs from "components/Common/Breadcrumb"
import { get, baseURL } from "helpers/api_helper"
import { AUDIT_LOGS_URL, AUDIT_STATS_URL, AUDIT_FILTERS_URL } from "helpers/url_helper"
import Notifications from "components/Notifications"
import moment from "moment"

const ACTION_CONFIG = {
    lock_request:     { label: "Lock Request",   icon: "bx bx-lock-alt",         bg: "#fffbeb", color: "#d97706", border: "#fde68a" },
    unlock_granted:   { label: "Unlock Granted", icon: "bx bx-lock-open-alt",    bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0" },
    lock_rejected:    { label: "Lock Rejected",  icon: "bx bx-x-circle",         bg: "#fef2f2", color: "#dc2626", border: "#fecaca" },
    update:           { label: "Data Update",    icon: "bx bx-edit-alt",         bg: "#f5f3ff", color: "#7c3aed", border: "#ddd6fe" },
    create:           { label: "Created",        icon: "bx bx-plus-circle",      bg: "#ecfdf5", color: "#059669", border: "#a7f3d0" },
    delete:           { label: "Deleted",        icon: "bx bx-trash",            bg: "#fff1f2", color: "#e11d48", border: "#fecdd3" },
    status_change:    { label: "Status Change",  icon: "bx bx-transfer-alt",     bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" },
    approve:          { label: "Approved",       icon: "bx bx-check-double",     bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
    hold:             { label: "Put On Hold",    icon: "bx bx-pause-circle",     bg: "#fff7ed", color: "#ea580c", border: "#ffedd5" },
    unhold:           { label: "Released Hold",  icon: "bx bx-play-circle",      bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0" },
    read:             { label: "Viewed / Read",  icon: "bx bx-show",             bg: "#f0f9ff", color: "#0284c7", border: "#bae6fd" },
    open:             { label: "Opened Record",  icon: "bx bx-folder-open",      bg: "#f0fdfa", color: "#0d9488", border: "#99f6e4" },
    view:             { label: "Viewed Details", icon: "bx bx-show-alt",         bg: "#f0f9ff", color: "#0284c7", border: "#bae6fd" },
    print:            { label: "Printed / PDF",  icon: "bx bx-printer",          bg: "#fdf4ff", color: "#a21caf", border: "#f5d0fe" },
    discount_override:{ label: "Discount Adjusted", icon: "bx bx-purchase-tag",  bg: "#fdf4ff", color: "#9333ea", border: "#f0abfc" },
};

const getActionBadge = (actionKey) => {
    const act = ACTION_CONFIG[actionKey] || { label: actionKey?.replace(/_/g, " ")?.toUpperCase(), icon: "bx bx-info-circle", bg: "#f1f5f9", color: "#475569", border: "#cbd5e1" };
    return (
        <span style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "5px",
            padding: "4px 10px",
            borderRadius: "6px",
            fontSize: "11px",
            fontWeight: "700",
            background: act.bg,
            color: act.color,
            border: "1px solid " + act.border,
            textTransform: "capitalize",
            whiteSpace: "nowrap"
        }}>
            <i className={act.icon} style={{ fontSize: "13px" }}></i>
            {act.label}
        </span>
    );
};

const getModuleBadge = (moduleKey) => {
    const colors = {
        orders:    { bg: "#eff6ff", color: "#1d4ed8" },
        invoices:  { bg: "#faf5ff", color: "#6b21a8" },
        approvals: { bg: "#fffbeb", color: "#b45309" },
        employee:  { bg: "#f0fdf4", color: "#15803d" },
        settings:  { bg: "#f1f5f9", color: "#334155" },
        masters:   { bg: "#ecfeff", color: "#0e7490" },
        clients:   { bg: "#fdf4ff", color: "#a21caf" },
    };
    const c = colors[moduleKey] || { bg: "#f8fafc", color: "#64748b" };
    return (
        <span style={{
            padding: "3px 8px",
            borderRadius: "4px",
            fontSize: "11px",
            fontWeight: "800",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            background: c.bg,
            color: c.color,
        }}>
            {moduleKey}
        </span>
    );
};

const getModuleDestination = (module, recordId) => {
    switch (module) {
        case "orders":
            return "/orders";
        case "invoices":
            return "/invoices";
        case "clients":
            return "/clients";
        case "employee":
            return "/employees";
        case "challans":
            return "/challans";
        case "settings":
            return "/settings/company";
        case "masters":
            return "/dashboard";
        default:
            return "/dashboard";
    }
};

const AuditLogs = () => {
    const [logs, setLogs] = useState([]);
    const [stats, setStats] = useState(null);
    const [filterOptions, setFilterOptions] = useState({ modules: [], actions: [] });
    const [loading, setLoading] = useState(false);
    const [statsLoading, setStatsLoading] = useState(false);

    // Filters State
    const [search, setSearch] = useState("");
    const [selectedModule, setSelectedModule] = useState("all");
    const [selectedAction, setSelectedAction] = useState("all");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRows, setTotalRows] = useState(0);
    const limit = 20;

    // Diff Modal State
    const [selectedLog, setSelectedLog] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showRawJson, setShowRawJson] = useState(false);

    const fetchStats = async () => {
        setStatsLoading(true);
        const res = await get(AUDIT_STATS_URL);
        if (res && res.status === 200) {
            setStats(res.response);
        }
        setStatsLoading(false);
    };

    const fetchFilterOptions = async () => {
        const res = await get(AUDIT_FILTERS_URL);
        if (res && res.status === 200) {
            setFilterOptions(res.response || { modules: [], actions: [] });
        }
    };

    const fetchLogs = useCallback(async (page = 1) => {
        setLoading(true);
        const params = new URLSearchParams({
            page,
            limit,
            search,
            module: selectedModule,
            action: selectedAction,
            from_date: fromDate,
            to_date: toDate
        });

        const res = await get(AUDIT_LOGS_URL + "?" + params.toString());
        if (res && res.status === 200) {
            setLogs(res.response?.rows || []);
            setTotalPages(res.response?.total_pages || 1);
            setTotalRows(res.response?.total_rows || 0);
            setCurrentPage(page);
        } else if (res && res.status === 403) {
            Notifications("Access Denied: Audit Logs are restricted to Super Administrators.", "error");
        }
        setLoading(false);
    }, [search, selectedModule, selectedAction, fromDate, toDate]);

    useEffect(() => {
        fetchStats();
        fetchFilterOptions();
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchLogs(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [fetchLogs]);

    const handleExport = () => {
        const params = new URLSearchParams({
            module: selectedModule,
            action: selectedAction,
            from_date: fromDate,
            to_date: toDate
        });
        window.open(baseURL + AUDIT_LOGS_URL.replace("/logs", "/export") + "?" + params.toString(), "_blank");
    };

    const openDiffModal = (log) => {
        setSelectedLog(log);
        setShowRawJson(false);
        setIsModalOpen(true);
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <MetaTags>
                    <title>Audit Logs | Superadmin Security & Compliance</title>
                </MetaTags>
                <Container fluid>
                    <Breadcrumbs title="Security & Compliance" breadcrumbItem="Audit Trail & Lock Logs" />

                    {/* TOP STATS CARDS */}
                    <Row className="mb-4">
                        <Col xl={3} md={6}>
                            <Card className="mini-stats-wid shadow-sm border-0" style={{ borderRadius: "12px", background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)", color: "#fff" }}>
                                <CardBody>
                                    <div className="d-flex align-items-center">
                                        <div className="flex-grow-1">
                                            <p className="text-white-50 fw-semibold text-uppercase mb-1" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>Actions Today</p>
                                            <h3 className="mb-0 fw-bold text-white">{statsLoading ? <i className="fas fa-spinner fa-spin" /> : stats?.today_total ?? 0}</h3>
                                            <span style={{ fontSize: "11px", color: "#94a3b8" }}>Total Recorded: <strong>{stats?.total_all_time ?? 0}</strong></span>
                                        </div>
                                        <div className="avatar-sm rounded-circle d-flex align-items-center justify-content-center" style={{ background: "rgba(255,255,255,0.1)" }}>
                                            <i className="bx bx-history" style={{ fontSize: "22px", color: "#38bdf8" }}></i>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>

                        <Col xl={3} md={6}>
                            <Card className="mini-stats-wid shadow-sm border-0" style={{ borderRadius: "12px", background: "#fff" }}>
                                <CardBody>
                                    <div className="d-flex align-items-center">
                                        <div className="flex-grow-1">
                                            <p className="text-muted fw-semibold text-uppercase mb-1" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>Edit Locks Requested</p>
                                            <h3 className="mb-0 fw-bold" style={{ color: "#d97706" }}>{statsLoading ? <i className="fas fa-spinner fa-spin" /> : stats?.locks_today ?? 0}</h3>
                                            <span style={{ fontSize: "11px", color: "#64748b" }}>Hold & Lock requests today</span>
                                        </div>
                                        <div className="avatar-sm rounded-circle d-flex align-items-center justify-content-center" style={{ background: "#fef3c7" }}>
                                            <i className="bx bx-lock-alt" style={{ fontSize: "22px", color: "#d97706" }}></i>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>

                        <Col xl={3} md={6}>
                            <Card className="mini-stats-wid shadow-sm border-0" style={{ borderRadius: "12px", background: "#fff" }}>
                                <CardBody>
                                    <div className="d-flex align-items-center">
                                        <div className="flex-grow-1">
                                            <p className="text-muted fw-semibold text-uppercase mb-1" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>Unlocks & Approvals</p>
                                            <h3 className="mb-0 fw-bold" style={{ color: "#16a34a" }}>{statsLoading ? <i className="fas fa-spinner fa-spin" /> : stats?.unlocks_today ?? 0}</h3>
                                            <span style={{ fontSize: "11px", color: "#64748b" }}>Granted tokens & approvals</span>
                                        </div>
                                        <div className="avatar-sm rounded-circle d-flex align-items-center justify-content-center" style={{ background: "#dcfce7" }}>
                                            <i className="bx bx-lock-open-alt" style={{ fontSize: "22px", color: "#16a34a" }}></i>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>

                        <Col xl={3} md={6}>
                            <Card className="mini-stats-wid shadow-sm border-0" style={{ borderRadius: "12px", background: "#fff" }}>
                                <CardBody>
                                    <div className="d-flex align-items-center">
                                        <div className="flex-grow-1">
                                            <p className="text-muted fw-semibold text-uppercase mb-1" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>Data Changes Today</p>
                                            <h3 className="mb-0 fw-bold" style={{ color: "#7c3aed" }}>{statsLoading ? <i className="fas fa-spinner fa-spin" /> : stats?.updates_today ?? 0}</h3>
                                            <span style={{ fontSize: "11px", color: "#64748b" }}>Pre-update modifications logged</span>
                                        </div>
                                        <div className="avatar-sm rounded-circle d-flex align-items-center justify-content-center" style={{ background: "#ede9fe" }}>
                                            <i className="bx bx-edit-alt" style={{ fontSize: "22px", color: "#7c3aed" }}></i>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* MAIN LOGS CARD */}
                    <Card className="shadow-sm border-0" style={{ borderRadius: "14px" }}>
                        <CardBody className="p-4">

                            {/* FILTERS TOOLBAR */}
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4 pb-3 border-bottom">
                                <div className="d-flex flex-wrap align-items-center gap-2" style={{ flex: 1, minWidth: "280px" }}>
                                    <div style={{ position: "relative", minWidth: "240px" }}>
                                        <i className="bx bx-search" style={{ position: "absolute", left: "12px", top: "11px", color: "#94a3b8", fontSize: "16px" }}></i>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search record ID, title, user..."
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            style={{ paddingLeft: "36px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "13px" }}
                                        />
                                    </div>

                                    <select
                                        className="form-select"
                                        value={selectedModule}
                                        onChange={(e) => setSelectedModule(e.target.value)}
                                        style={{ width: "150px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "13px" }}
                                    >
                                        <option value="all">All Modules</option>
                                        {filterOptions.modules?.map((m) => (
                                            <option key={m} value={m}>{m.toUpperCase()}</option>
                                        ))}
                                    </select>

                                    <select
                                        className="form-select"
                                        value={selectedAction}
                                        onChange={(e) => setSelectedAction(e.target.value)}
                                        style={{ width: "160px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "13px" }}
                                    >
                                        <option value="all">All Actions</option>
                                        {filterOptions.actions?.map((a) => (
                                            <option key={a} value={a}>{a.replace(/_/g, " ").toUpperCase()}</option>
                                        ))}
                                    </select>

                                    <input
                                        type="date"
                                        className="form-control"
                                        value={fromDate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                        style={{ width: "140px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "12px" }}
                                        title="From Date"
                                    />
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={toDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                        style={{ width: "140px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "12px" }}
                                        title="To Date"
                                    />

                                    {(search || selectedModule !== "all" || selectedAction !== "all" || fromDate || toDate) && (
                                        <button
                                            className="btn btn-light"
                                            onClick={() => {
                                                setSearch("");
                                                setSelectedModule("all");
                                                setSelectedAction("all");
                                                setFromDate("");
                                                setToDate("");
                                            }}
                                            style={{ borderRadius: "8px", fontSize: "12px" }}
                                            title="Clear Filters"
                                        >
                                            <i className="bx bx-x me-1"></i> Reset
                                        </button>
                                    )}
                                </div>

                                <div className="d-flex align-items-center gap-2">
                                    <button
                                        className="btn btn-outline-secondary d-flex align-items-center gap-1"
                                        onClick={() => { fetchLogs(currentPage); fetchStats(); }}
                                        style={{ borderRadius: "8px", fontSize: "13px" }}
                                    >
                                        <i className="bx bx-refresh" style={{ fontSize: "16px" }}></i> Refresh
                                    </button>

                                    <button
                                        className="btn btn-success d-flex align-items-center gap-1"
                                        onClick={handleExport}
                                        style={{ borderRadius: "8px", fontSize: "13px", fontWeight: "600" }}
                                    >
                                        <i className="bx bx-export" style={{ fontSize: "16px" }}></i> Export CSV
                                    </button>
                                </div>
                            </div>

                            {/* LOGS TABLE */}
                            <div className="table-responsive" style={{ minHeight: "400px" }}>
                                <table className="table table-hover align-middle mb-0">
                                    <thead style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                                        <tr>
                                            <th style={{ width: "160px", fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>Timestamp</th>
                                            <th style={{ width: "135px", fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>Action</th>
                                            <th style={{ width: "100px", fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>Module</th>
                                            <th style={{ fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>Record / Subject</th>
                                            <th style={{ fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>Summary Description</th>
                                            <th style={{ width: "180px", fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>User / Actor</th>
                                            <th style={{ width: "110px", textAlign: "center", fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>Changes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="7" className="text-center py-5">
                                                    <i className="fas fa-spinner fa-spin text-primary" style={{ fontSize: "28px" }}></i>
                                                    <p className="mt-2 text-muted mb-0" style={{ fontSize: "13px" }}>Loading audit records...</p>
                                                </td>
                                            </tr>
                                        ) : logs.length === 0 ? (
                                            <tr>
                                                <td colSpan="7" className="text-center py-5">
                                                    <div className="avatar-md mx-auto mb-3 rounded-circle d-flex align-items-center justify-content-center" style={{ background: "#f1f5f9" }}>
                                                        <i className="bx bx-check-shield" style={{ fontSize: "32px", color: "#94a3b8" }}></i>
                                                    </div>
                                                    <h5 className="text-muted fw-bold">No Audit Records Found</h5>
                                                    <p className="text-muted mb-0" style={{ fontSize: "12px" }}>Try adjusting your filters or search query.</p>
                                                </td>
                                            </tr>
                                        ) : (
                                            logs.map((log) => {
                                                const hasDiff = (log.diff_values && Object.keys(log.diff_values).length > 0) || (log.old_values && log.new_values);
                                                const destUrl = getModuleDestination(log.module, log.record_id);

                                                return (
                                                    <tr key={log.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                                                        <td>
                                                            <div style={{ fontWeight: "700", fontSize: "13px", color: "#1e293b" }}>
                                                                {moment(log.created_at).format("DD MMM YYYY")}
                                                            </div>
                                                            <div style={{ fontSize: "11px", color: "#94a3b8" }}>
                                                                {moment(log.created_at).format("hh:mm:ss A")} &nbsp;({moment(log.created_at).fromNow()})
                                                            </div>
                                                        </td>

                                                        <td>{getActionBadge(log.action)}</td>

                                                        <td>{getModuleBadge(log.module)}</td>

                                                        {/* Clickable Record / Subject Link */}
                                                        <td>
                                                            <div>
                                                                <Link
                                                                    to={destUrl}
                                                                    style={{ fontWeight: "700", fontSize: "13px", color: "#2563eb", display: "inline-flex", alignItems: "center", gap: "4px", textDecoration: "none" }}
                                                                    title={"Open " + log.module + " page"}
                                                                >
                                                                    {log.record_title || log.record_id}
                                                                    <i className="bx bx-link-external" style={{ fontSize: "12px", opacity: 0.7 }}></i>
                                                                </Link>
                                                            </div>
                                                            <div style={{ fontSize: "11px", color: "#64748b", fontFamily: "monospace", marginTop: "2px" }}>
                                                                ID: {log.record_id}
                                                            </div>
                                                        </td>

                                                        <td>
                                                            <div style={{ fontSize: "13px", color: "#334155", maxWidth: "340px", lineHeight: "1.4" }}>
                                                                {log.description}
                                                            </div>
                                                        </td>

                                                        <td>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <div style={{
                                                                    width: "30px",
                                                                    height: "30px",
                                                                    borderRadius: "50%",
                                                                    background: "linear-gradient(135deg, #4f46e5, #3b82f6)",
                                                                    color: "#fff",
                                                                    fontWeight: "700",
                                                                    fontSize: "11px",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    flexShrink: 0
                                                                }}>
                                                                    {(log.user_fullname || log.username || "U").slice(0, 2).toUpperCase()}
                                                                </div>
                                                                <div style={{ overflow: "hidden" }}>
                                                                    <div style={{ fontWeight: "700", fontSize: "12px", color: "#1e293b", whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>
                                                                        {log.user_fullname || log.username || "System"}
                                                                    </div>
                                                                    <div style={{ fontSize: "10px", color: "#94a3b8" }}>
                                                                        {log.user_role || "User"} • IP: {log.ip_address || "—"}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>

                                                        <td style={{ textAlign: "center" }}>
                                                            {hasDiff ? (
                                                                <button
                                                                    className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                                                                    onClick={() => openDiffModal(log)}
                                                                    style={{ borderRadius: "6px", fontSize: "11px", fontWeight: "700", padding: "4px 8px" }}
                                                                    title="Inspect field-by-field diff"
                                                                >
                                                                    <i className="bx bx-git-compare"></i> Diff
                                                                </button>
                                                            ) : (
                                                                <button
                                                                    className="btn btn-sm btn-light d-inline-flex align-items-center gap-1"
                                                                    onClick={() => openDiffModal(log)}
                                                                    style={{ borderRadius: "6px", fontSize: "11px", color: "#64748b", padding: "4px 8px" }}
                                                                    title="View record snapshot"
                                                                >
                                                                    <i className="bx bx-show"></i> View
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* PAGINATION */}
                            {!loading && totalRows > 0 && (
                                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-4 pt-3 border-top">
                                    <div style={{ fontSize: "13px", color: "#64748b" }}>
                                        Showing <strong>{(currentPage - 1) * limit + 1}</strong> to <strong>{Math.min(currentPage * limit, totalRows)}</strong> of <strong>{totalRows}</strong> audit events
                                    </div>
                                    <div className="d-flex gap-1">
                                        <button
                                            className="btn btn-sm btn-light"
                                            disabled={currentPage <= 1}
                                            onClick={() => fetchLogs(currentPage - 1)}
                                            style={{ borderRadius: "6px", padding: "6px 12px", fontWeight: "600" }}
                                        >
                                            <i className="bx bx-chevron-left"></i> Prev
                                        </button>

                                        <span className="btn btn-sm btn-primary" style={{ borderRadius: "6px", padding: "6px 14px", fontWeight: "700" }}>
                                            {currentPage} / {totalPages}
                                        </span>

                                        <button
                                            className="btn btn-sm btn-light"
                                            disabled={currentPage >= totalPages}
                                            onClick={() => fetchLogs(currentPage + 1)}
                                            style={{ borderRadius: "6px", padding: "6px 12px", fontWeight: "600" }}
                                        >
                                            Next <i className="bx bx-chevron-right"></i>
                                        </button>
                                    </div>
                                </div>
                            )}

                        </CardBody>
                    </Card>
                </Container>
            </div>

            {/* DIFF INSPECTOR MODAL */}
            <Modal isOpen={isModalOpen} toggle={() => setIsModalOpen(false)} size="lg" centered>
                <ModalHeader toggle={() => setIsModalOpen(false)} style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className="bx bx-shield-quarter text-primary" style={{ fontSize: "20px" }}></i>
                        <span style={{ fontWeight: "800", fontSize: "15px", color: "#0f172a" }}>
                            Audit Record #{selectedLog?.id} — {selectedLog?.record_title}
                        </span>
                    </div>
                </ModalHeader>
                <ModalBody className="p-4" style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    {selectedLog && (
                        <div>
                            <div style={{ background: "#f8fafc", borderRadius: "10px", padding: "16px", marginBottom: "20px", border: "1px solid #e2e8f0" }}>
                                <Row className="g-3">
                                    <Col md={3}>
                                        <div style={{ fontSize: "11px", fontWeight: "700", color: "#94a3b8", textTransform: "uppercase" }}>Action</div>
                                        <div className="mt-1">{getActionBadge(selectedLog.action)}</div>
                                    </Col>
                                    <Col md={3}>
                                        <div style={{ fontSize: "11px", fontWeight: "700", color: "#94a3b8", textTransform: "uppercase" }}>Module</div>
                                        <div className="mt-1">{getModuleBadge(selectedLog.module)}</div>
                                    </Col>
                                    <Col md={3}>
                                        <div style={{ fontSize: "11px", fontWeight: "700", color: "#94a3b8", textTransform: "uppercase" }}>Performed By</div>
                                        <div style={{ fontWeight: "700", fontSize: "13px", color: "#1e293b", marginTop: "4px" }}>
                                            {selectedLog.user_fullname || selectedLog.username}
                                        </div>
                                        <div style={{ fontSize: "11px", color: "#64748b" }}>Role: {selectedLog.user_role || "User"}</div>
                                    </Col>
                                    <Col md={3}>
                                        <div style={{ fontSize: "11px", fontWeight: "700", color: "#94a3b8", textTransform: "uppercase" }}>When & Where</div>
                                        <div style={{ fontWeight: "600", fontSize: "12px", color: "#1e293b", marginTop: "4px" }}>
                                            {moment(selectedLog.created_at).format("DD MMM YYYY, hh:mm A")}
                                        </div>
                                        <div style={{ fontSize: "11px", color: "#64748b" }}>IP: {selectedLog.ip_address || "—"}</div>
                                    </Col>
                                </Row>

                                <div style={{ borderTop: "1px solid #e2e8f0", marginTop: "12px", paddingTop: "10px", fontSize: "12px", color: "#475569" }}>
                                    <strong>Description:</strong> {selectedLog.description}
                                </div>
                            </div>

                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 style={{ fontWeight: "800", color: "#1e293b", margin: 0 }}>
                                    {showRawJson ? "Raw Snapshot Data" : "Field-by-Field Changes (Diff)"}
                                </h6>
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => setShowRawJson(prev => !prev)}
                                    style={{ borderRadius: "6px", fontSize: "11px", fontWeight: "600" }}
                                >
                                    {showRawJson ? "Show Formatted Diff" : "Show Raw JSON"}
                                </button>
                            </div>

                            {!showRawJson ? (
                                selectedLog.diff_values && Object.keys(selectedLog.diff_values).length > 0 ? (
                                    <div className="table-responsive border rounded" style={{ overflow: "hidden" }}>
                                        <table className="table table-bordered mb-0">
                                            <thead style={{ background: "#f1f5f9" }}>
                                                <tr>
                                                    <th style={{ width: "25%", fontSize: "11px", fontWeight: "800", color: "#475569" }}>Field Name</th>
                                                    <th style={{ width: "37.5%", fontSize: "11px", fontWeight: "800", color: "#dc2626", background: "#fef2f2" }}>Previous Value (Before)</th>
                                                    <th style={{ width: "37.5%", fontSize: "11px", fontWeight: "800", color: "#16a34a", background: "#f0fdf4" }}>New Value (After)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Object.entries(selectedLog.diff_values).map(([fieldName, delta]) => {
                                                    const formatVal = (v) => {
                                                        if (v === null || v === undefined) return <span style={{ color: "#94a3b8", fontStyle: "italic" }}>null</span>;
                                                        if (typeof v === "object") return <pre style={{ margin: 0, fontSize: "11px", whiteSpace: "pre-wrap" }}>{JSON.stringify(v, null, 2)}</pre>;
                                                        return String(v);
                                                    };
                                                    return (
                                                        <tr key={fieldName}>
                                                            <td style={{ fontWeight: "700", fontSize: "12px", color: "#334155", background: "#f8fafc" }}>
                                                                {fieldName.replace(/_/g, " ").toUpperCase()}
                                                            </td>
                                                            <td style={{ background: "#fff5f5", color: "#991b1b", fontSize: "12px" }}>
                                                                {formatVal(delta?.old)}
                                                            </td>
                                                            <td style={{ background: "#f0fdf4", color: "#166534", fontSize: "12px", fontWeight: "600" }}>
                                                                {formatVal(delta?.new)}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : selectedLog.old_values || selectedLog.new_values ? (
                                    <div style={{ background: "#f8fafc", borderRadius: "8px", padding: "16px", border: "1px solid #e2e8f0" }}>
                                        <p style={{ fontSize: "12px", color: "#64748b", marginBottom: "12px" }}>
                                            Full record snapshot recorded. No isolated field delta calculated.
                                        </p>
                                        <Row>
                                            {selectedLog.old_values && (
                                                <Col md={selectedLog.new_values ? 6 : 12}>
                                                    <div style={{ fontSize: "11px", fontWeight: "700", color: "#dc2626", marginBottom: "6px" }}>BEFORE STATE</div>
                                                    <pre style={{ background: "#fff", padding: "12px", borderRadius: "6px", border: "1px solid #fecaca", fontSize: "11px", maxHeight: "240px", overflow: "auto" }}>
                                                        {JSON.stringify(selectedLog.old_values, null, 2)}
                                                    </pre>
                                                </Col>
                                            )}
                                            {selectedLog.new_values && (
                                                <Col md={selectedLog.old_values ? 6 : 12}>
                                                    <div style={{ fontSize: "11px", fontWeight: "700", color: "#16a34a", marginBottom: "6px" }}>AFTER STATE</div>
                                                    <pre style={{ background: "#fff", padding: "12px", borderRadius: "6px", border: "1px solid #bbf7d0", fontSize: "11px", maxHeight: "240px", overflow: "auto" }}>
                                                        {JSON.stringify(selectedLog.new_values, null, 2)}
                                                    </pre>
                                                </Col>
                                            )}
                                        </Row>
                                    </div>
                                ) : (
                                    <div style={{ textAlign: "center", padding: "32px", background: "#f8fafc", borderRadius: "8px", color: "#94a3b8", fontSize: "13px" }}>
                                        No before/after payload diff recorded for this action.
                                    </div>
                                )
                            ) : (
                                <pre style={{ background: "#0f172a", color: "#38bdf8", padding: "16px", borderRadius: "8px", fontSize: "11px", maxHeight: "350px", overflow: "auto" }}>
                                    {JSON.stringify(selectedLog, null, 2)}
                                </pre>
                            )}
                        </div>
                    )}
                </ModalBody>
                <ModalFooter style={{ background: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
                    <Button color="secondary" onClick={() => setIsModalOpen(false)} style={{ borderRadius: "8px", fontSize: "13px", fontWeight: "600" }}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>
        </React.Fragment>
    );
};

export default AuditLogs;
